/**
 * <RagAnalysisPanel> — "Analyser avec l'IA" grounded RAG answer.
 *
 * Asks a question against the knowledge base (optionally scoped to an entity)
 * and renders a grounded answer + clickable citations. Uses either
 * /knowledge/entities/analyze (source="analyze", default) or
 * /ai/copilot/advise (source="advise") which returns structured advice items.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Sparkles,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  ArrowRight,
  BookOpen,
  Clock,
  FileUp,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  useRagAnalyze,
  useCopilotAdvise,
  type KnowledgeCitation,
  type CopilotAdvice,
} from '@/services/copilot.service'
import { getRagSessionId } from '@/lib/rag-session'
import { DocumentAnalyzer } from '@/components/shared/copilot/DocumentAnalyzer'

interface RagAnalysisPanelProps {
  module: string
  entityType?: string
  entityId?: string
  /** Pre-filled question. */
  defaultQuestion?: string
  /** 'analyze' (grounded answer) or 'advise' (structured advice items). */
  source?: 'analyze' | 'advise'
  /** Compact rendering for narrow containers (e.g. the copilot sidebar). */
  embedded?: boolean
  className?: string
}

const PRIORITY_TONE: Record<string, string> = {
  critical: 'bg-red-500/10 text-red-600 border-red-500/30',
  high: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  medium: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  low: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
}

function Citations({
  citations,
  onOpen,
}: {
  citations: KnowledgeCitation[]
  onOpen: (c: KnowledgeCitation) => void
}) {
  if (!citations || citations.length === 0) return null
  return (
    <div className="mt-3 border-t pt-2">
      <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <BookOpen className="h-3.5 w-3.5" /> Sources
      </p>
      <div className="flex flex-wrap gap-1.5">
        {citations.map((c) => (
          <button
            key={`${c.entity_type}-${c.entity_id}-${c.n}`}
            onClick={() => onOpen(c)}
            disabled={!c.url}
            data-scope={c.scope ?? 'permanent'}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors',
              c.url
                ? 'cursor-pointer hover:bg-accent'
                : 'cursor-default opacity-80',
            )}
            title={`${c.url ? 'Ouvrir la source' : c.title ?? ''}${
              c.scope === 'session'
                ? ' — source temporaire (session, purgée automatiquement)'
                : ''
            }`}
          >
            <span className="font-mono font-semibold text-primary">[{c.n}]</span>
            <span className="max-w-[160px] truncate">{c.title}</span>
            {/* TRUST layer (subsystem J): label each source's scope */}
            {c.scope === 'session' && (
              <span
                data-testid="citation-scope-session"
                className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 text-[10px] font-medium text-amber-600"
              >
                <Clock className="h-2.5 w-2.5" /> session
              </span>
            )}
            {c.url && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
          </button>
        ))}
      </div>
    </div>
  )
}

export function RagAnalysisPanel({
  module,
  entityType,
  entityId,
  defaultQuestion = '',
  source = 'analyze',
  embedded = false,
  className,
}: RagAnalysisPanelProps) {
  const navigate = useNavigate()
  const [question, setQuestion] = useState(defaultQuestion)

  const analyzeMutation = useRagAnalyze()
  const adviseMutation = useCopilotAdvise()

  const isPending = analyzeMutation.isPending || adviseMutation.isPending
  const analysis = analyzeMutation.data
  const advice = adviseMutation.data
  const errored = analyzeMutation.isError || adviseMutation.isError

  const grounded = source === 'advise' ? advice?.grounded : analysis?.grounded
  const citations: KnowledgeCitation[] =
    (source === 'advise' ? advice?.citations : analysis?.citations) ?? []

  const run = () => {
    if (source === 'advise') {
      adviseMutation.mutate({
        module,
        entity_type: entityType,
        entity_id: entityId,
        question: question.trim() || undefined,
      })
    } else {
      analyzeMutation.mutate({
        question: question.trim() || 'Analyse les informations et donne les points clés.',
        entity_type: entityType,
        entity_id: entityId,
        // TRUST layer (subsystem J): also search the caller's session-scoped
        // temporary docs (never other sessions'). Permanent docs unaffected.
        session_id: getRagSessionId(),
      })
    }
  }

  const openCitation = (c: KnowledgeCitation) => {
    if (c.url) navigate(c.url)
  }

  return (
    <div className={cn('rounded-lg border bg-card', embedded ? 'p-3' : 'p-4', className)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Sparkles className="h-4 w-4 text-primary" />
          Analyser avec l'IA
        </div>
        <div className="flex items-center gap-1.5">
          {/* TRUST layer (subsystem J): scope-aware document upload */}
          <DocumentAnalyzer
            moduleContext={module}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                data-testid="rag-upload-doc"
                title="Ajouter un document à la base RAG (permanent ou temporaire)"
              >
                <FileUp className="h-3.5 w-3.5" />
                Document
              </Button>
            }
          />
        </div>
        {grounded != null && (analysis || advice) && (
          <Badge
            variant="outline"
            className={cn(
              'gap-1 text-[10px]',
              grounded
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600'
                : 'border-amber-500/30 bg-amber-500/10 text-amber-600',
            )}
          >
            {grounded ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
            {grounded ? 'Ancré (RAG)' : 'Non ancré'}
          </Badge>
        )}
      </div>

      <Textarea
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder={
          entityType
            ? 'Question sur cet élément (laisser vide pour une analyse générale)…'
            : 'Posez une question à analyser sur le contexte…'
        }
        rows={embedded ? 2 : 3}
        className="text-sm"
      />

      <div className="mt-2 flex items-center justify-end">
        <Button size="sm" onClick={run} disabled={isPending} className="gap-1.5">
          {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Analyser
        </Button>
      </div>

      {errored && (
        <p className="mt-3 rounded-md bg-amber-500/10 p-2 text-xs text-amber-600">
          L'analyse IA est momentanément indisponible. Réessayez plus tard.
        </p>
      )}

      {/* analyze: grounded answer */}
      {source === 'analyze' && analysis && (
        <div className="mt-3">
          <div className="whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-sm leading-relaxed">
            {analysis.answer}
          </div>
          <Citations citations={citations} onOpen={openCitation} />
        </div>
      )}

      {/* advise: structured advice items */}
      {source === 'advise' && advice && (
        <div className="mt-3 space-y-2">
          {advice.advice.map((a: CopilotAdvice, i: number) => (
            <div key={i} className="rounded-md border p-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">{a.title}</p>
                <Badge
                  variant="outline"
                  className={cn('text-[10px]', PRIORITY_TONE[a.priority?.toLowerCase()] ?? '')}
                >
                  {a.priority}
                </Badge>
              </div>
              {a.detail && <p className="mt-1 text-xs text-muted-foreground">{a.detail}</p>}
            </div>
          ))}
          <Citations citations={citations} onOpen={openCitation} />
        </div>
      )}
    </div>
  )
}

export default RagAnalysisPanel
